import React from "react";
import {
  Box,
  Paper,
  Title,
  Stack,
  Text,
  Group,
  Button,
  TextInput,
  Modal,
} from "@mantine/core";
import { PdfDropzone } from "../PdfDropzone";

type UploadStepProps = {
  pdfArrayBuffer: ArrayBuffer | null;
  numPages: number;
  metadata: { docTitle: string; author: string };
  setMetadata: (meta: { docTitle: string; author: string }) => void;
  loadPdf: (file: File) => void;
  onClearPdf: () => void;
  onContinue: () => void;
  conflictModalOpen: boolean;
  setConflictModalOpen: (open: boolean) => void;
  conflictFile: File | null;
  setConflictFile: (file: File | null) => void;
  onNavigate?: (tab: string, file?: File | null) => void;
};

export function UploadStep({
  pdfArrayBuffer,
  numPages,
  metadata,
  setMetadata,
  loadPdf,
  onClearPdf,
  onContinue,
  conflictModalOpen,
  setConflictModalOpen,
  conflictFile,
  setConflictFile,
  onNavigate,
}: UploadStepProps) {
  return (
    <div className="full-center-frame">
      <Box style={{ width: "100%", maxWidth: 800 }}>
        <div className="premium-wrapper">
          <Title order={2} mb="lg">
            Create New Document
          </Title>
          <Stack gap="lg">
            {!pdfArrayBuffer ? (
              <PdfDropzone
                onFileSelected={loadPdf}
                height={200}
                label="Drag PDF here or click to select"
              />
            ) : (
              <Stack>
                <Text size="sm" c="dimmed">
                  File uploaded
                </Text>
                <Group justify="space-between">
                  <Text fw={500}>Document loaded ({numPages} pages)</Text>
                  <Button variant="subtle" color="red" onClick={onClearPdf}>
                    Reset
                  </Button>
                </Group>
              </Stack>
            )}

            <TextInput
              label="Document Title"
              value={metadata.docTitle}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setMetadata({ ...metadata, docTitle: e.target.value })
              }
              disabled={!pdfArrayBuffer}
            />
            <TextInput
              label="Author Name"
              value={metadata.author}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setMetadata({ ...metadata, author: e.target.value })
              }
              disabled={!pdfArrayBuffer}
            />

            <Button
              fullWidth
              size="md"
              onClick={onContinue}
              disabled={!pdfArrayBuffer || !metadata.docTitle}
            >
              Continue to Editor
            </Button>
          </Stack>
        </div>
      </Box>

      <Modal
        opened={conflictModalOpen}
        onClose={() => {
          setConflictModalOpen(false);
          setConflictFile(null);
        }}
        title="Already a Signed+ Document"
        centered
      >
        <Text mb="md">
          This document already contains Signed+ metadata. The <b>Create</b>{" "}
          view is for preparing brand new documents.
        </Text>
        <Text mb="xl">
          Did you mean to sign it? You can go to the <b>Sign</b> view, or cancel
          to pick a different file.
        </Text>
        <Group justify="flex-end">
          <Button
            variant="default"
            onClick={() => {
              setConflictModalOpen(false);
              setConflictFile(null);
            }}
          >
            Cancel
          </Button>
          <Button
            color="blue"
            onClick={() => {
              if (onNavigate && conflictFile) {
                onNavigate("sign", conflictFile);
              }
              setConflictModalOpen(false);
              setConflictFile(null);
            }}
          >
            Go to Sign View
          </Button>
        </Group>
      </Modal>
    </div>
  );
}
